import { useEffect, useState } from "react";
import { AnimatePresence, motion } from "framer-motion";
import { Menu, X } from "lucide-react";
import { Monogram } from "./Monogram";

const LINKS = [
    { label: "About", href: "#about" },
    { label: "Services", href: "#services" },
    { label: "Our Craft", href: "#process" },
    { label: "Gallery", href: "#gallery" },
    { label: "Kind Words", href: "#testimonials" },
    { label: "FAQ", href: "#faq" },
];

export const Header = () => {
    const [scrolled, setScrolled] = useState(false);
    const [open, setOpen] = useState(false);

    useEffect(() => {
        const onScroll = () => setScrolled(window.scrollY > 50);
        onScroll();
        window.addEventListener("scroll", onScroll, { passive: true });
        return () => window.removeEventListener("scroll", onScroll);
    }, []);

    return (
        <header
            data-testid="site-header"
            className={`fixed top-0 left-0 right-0 z-50 transition-[background-color,box-shadow,backdrop-filter] duration-500 ease-elegant ${
                scrolled
                    ? "bg-emerald/95 backdrop-blur-md shadow-[0_10px_40px_-12px_rgba(10,43,33,0.6)]"
                    : "bg-transparent"
            }`}
        >
            <div className="mx-auto flex max-w-7xl items-center justify-between px-6 py-4 lg:px-10">
                <a href="#top" data-testid="header-logo-link" className="flex items-center gap-3">
                    <Monogram size={scrolled ? 44 : 56} className="transition-[width,height] duration-500" />
                    <span className="hidden sm:block">
                        <span className="block font-serif text-sm tracking-[0.35em] text-ivory">EVENTS BY</span>
                        <span className="block font-script text-2xl leading-none text-gold">Leemerh</span>
                    </span>
                </a>

                <nav className="hidden items-center gap-9 lg:flex" data-testid="desktop-nav">
                    {LINKS.map((l) => (
                        <a
                            key={l.href}
                            href={l.href}
                            data-testid={`nav-${l.label.toLowerCase().replace(/\s+/g, "-")}`}
                            className="nav-link text-[11px] font-medium uppercase tracking-[0.3em] text-ivory/85 hover:text-gold"
                        >
                            {l.label}
                        </a>
                    ))}
                    <a
                        href="#contact"
                        data-testid="nav-book-consultation-btn"
                        className="btn-sweep border border-gold px-6 py-2.5 text-[11px] font-semibold uppercase tracking-[0.3em] text-gold transition-colors duration-500 hover:text-ivory"
                    >
                        Book a Consultation
                    </a>
                </nav>

                <button
                    data-testid="mobile-menu-toggle"
                    onClick={() => setOpen(!open)}
                    className="text-gold lg:hidden"
                    aria-label="Toggle menu"
                >
                    {open ? <X size={26} /> : <Menu size={26} />}
                </button>
            </div>

            <AnimatePresence>
                {open && (
                    <motion.nav
                        data-testid="mobile-nav"
                        initial={{ opacity: 0, height: 0 }}
                        animate={{ opacity: 1, height: "auto" }}
                        exit={{ opacity: 0, height: 0 }}
                        transition={{ duration: 0.45, ease: [0.22, 1, 0.36, 1] }}
                        className="overflow-hidden border-t border-gold/20 bg-emerald/97 backdrop-blur-lg lg:hidden"
                    >
                        <div className="flex flex-col gap-1 px-8 py-6">
                            {LINKS.map((l) => (
                                <a
                                    key={l.href}
                                    href={l.href}
                                    data-testid={`mobile-nav-${l.label.toLowerCase().replace(/\s+/g, "-")}`}
                                    onClick={() => setOpen(false)}
                                    className="border-b border-gold/10 py-3.5 text-xs uppercase tracking-[0.3em] text-ivory/85 transition-colors duration-300 hover:text-gold"
                                >
                                    {l.label}
                                </a>
                            ))}
                            <a
                                href="#contact"
                                data-testid="mobile-nav-book-btn"
                                onClick={() => setOpen(false)}
                                className="mt-4 border border-gold py-3 text-center text-xs font-semibold uppercase tracking-[0.3em] text-gold"
                            >
                                Book a Consultation
                            </a>
                        </div>
                    </motion.nav>
                )}
            </AnimatePresence>
        </header>
    );
};
